import { FEATURE_KEYS, MODULE_KEYS } from '../routes/ids.js';
import { SETTLEMENT_INSTRUCTION_TABLE } from './table-registry.js';
const tk = (value) => value;
const fieldId = (value) => value;
function metadata(id) {
    return {
        id: id,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTLEMENT,
        feature: FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS,
        privacyClassification: 'CONFIDENTIAL',
        telemetryId: `experience.settlement.${id}`,
        accessibilityLabelKey: tk('card.settlement.title'),
        availability: 'AVAILABLE',
        owner: 'settlement',
    };
}
function field(name, type, required = false, options, state, rules = []) {
    const validationRules = [
        ...(required
            ? [{ ruleId: 'required', messageKey: tk('validation.required') }]
            : []),
        ...rules,
    ];
    return {
        id: fieldId(`field.settlement.${name}`),
        name,
        labelKey: tk(`card.settlement.fields.${name}`),
        fieldType: type,
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        ...(options ? { options } : {}),
        ...(validationRules.length > 0
            ? { validation: { rules: validationRules, validateWhen: 'submit' } }
            : {}),
        ...(state ? { state } : {}),
        layout: { md: 6 },
    };
}
function visibleWhenWorkflow(...kinds) {
    return {
        visibilityCondition: {
            operator: 'in',
            field: 'field.settlement.workflowKind',
            values: kinds,
        },
    };
}
function createOrCorrectState() {
    return {
        ...visibleWhenWorkflow('create', 'correct'),
        requiredCondition: {
            operator: 'equals',
            field: 'field.settlement.workflowKind',
            value: 'create',
        },
    };
}
function reasonWorkflowState() {
    return {
        ...visibleWhenWorkflow('confirm', 'correct', 'cancel'),
        requiredCondition: {
            operator: 'or',
            conditions: [
                {
                    operator: 'in',
                    field: 'field.settlement.workflowKind',
                    values: ['correct', 'cancel'],
                },
                {
                    operator: 'and',
                    conditions: [
                        {
                            operator: 'equals',
                            field: 'field.settlement.workflowKind',
                            value: 'confirm',
                        },
                        {
                            operator: 'equals',
                            field: 'field.settlement.status',
                            value: 'FAILED',
                        },
                    ],
                },
            ],
        },
    };
}
const decimalPrecision4 = {
    ruleId: 'decimalPrecision',
    messageKey: tk('validation.decimalPrecision'),
    params: { precision: 4 },
};
const SETTLEMENT_STATUS_OPTIONS = [
    { value: 'PARTIALLY_SETTLED', labelKey: tk('card.settlement.statuses.PARTIALLY_SETTLED') },
    { value: 'SETTLED', labelKey: tk('card.settlement.statuses.SETTLED') },
    { value: 'FAILED', labelKey: tk('card.settlement.statuses.FAILED') },
];
const workflowKindField = {
    id: fieldId('field.settlement.workflowKind'),
    name: 'workflowKind',
    labelKey: tk('card.settlement.fields.workflowKind'),
    fieldType: 'hidden',
    value: {
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'exclude',
        outputOnly: true,
        retainWhenHidden: true,
    },
    layout: { md: 12 },
};
function canonicalSettlementAction(name) {
    const actionId = `action.settlement.instructions.${name}`;
    const action = [
        ...(SETTLEMENT_INSTRUCTION_TABLE.pageActions ?? []),
        ...(SETTLEMENT_INSTRUCTION_TABLE.rowActions ?? []),
    ].find((candidate) => String(candidate.id) === actionId);
    if (!action?.commandId) {
        throw new Error(`Missing canonical executable Settlement action: ${actionId}`);
    }
    return action;
}
/**
 * Form actions inherit identity, operation, command, intent, idempotency, and
 * success/error semantics from the contracts-owned Settlement table action.
 * Only form-runtime behavior belongs here, preventing the table and form from
 * becoming independent authorities for the same lifecycle command.
 */
function saveAction(name) {
    const action = canonicalSettlementAction(name);
    return {
        ...action,
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: name === 'correct',
            requiresValid: true,
            resetBehavior: 'toServerValues',
        },
        visibilityCondition: {
            operator: 'equals',
            field: 'field.settlement.workflowKind',
            value: name,
        },
    };
}
export const SETTLEMENT_INSTRUCTION_FORM = {
    ...metadata('form.settlement.instruction'),
    id: 'form.settlement.instruction',
    titleKey: tk('card.settlement.title'),
    descriptionKey: tk('card.settlement.description'),
    mode: 'create',
    fields: [
        workflowKindField,
        field('transactionId', 'text', true, undefined, visibleWhenWorkflow('create')),
        field('expectedQuantity', 'decimal', false, undefined, createOrCorrectState(), [
            decimalPrecision4,
            { ruleId: 'min', messageKey: tk('validation.min'), params: { min: 0.0001 } },
        ]),
        field('expectedCash', 'decimal', false, undefined, createOrCorrectState(), [
            decimalPrecision4,
            { ruleId: 'min', messageKey: tk('validation.min'), params: { min: 0 } },
        ]),
        field('expectedDate', 'date', false, undefined, createOrCorrectState()),
        field('externalMessageId', 'text', true, undefined, visibleWhenWorkflow('confirm')),
        field('sequence', 'number', true, undefined, visibleWhenWorkflow('confirm'), [
            { ruleId: 'integer', messageKey: tk('validation.integer') },
            { ruleId: 'min', messageKey: tk('validation.min'), params: { min: 1 } },
        ]),
        field('quantity', 'decimal', true, undefined, visibleWhenWorkflow('confirm'), [
            decimalPrecision4,
            { ruleId: 'min', messageKey: tk('validation.min'), params: { min: 0 } },
        ]),
        field('cash', 'decimal', true, undefined, visibleWhenWorkflow('confirm'), [
            decimalPrecision4,
            { ruleId: 'min', messageKey: tk('validation.min'), params: { min: 0 } },
        ]),
        field('status', 'select', true, SETTLEMENT_STATUS_OPTIONS, visibleWhenWorkflow('confirm')),
        field('reason', 'textarea', false, undefined, reasonWorkflowState()),
    ],
    sections: [
        {
            id: 'section.settlement.create',
            titleKey: tk('card.settlement.actions.create'),
            fields: [
                'field.settlement.transactionId',
                'field.settlement.expectedQuantity',
                'field.settlement.expectedCash',
                'field.settlement.expectedDate',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
            visibilityCondition: {
                operator: 'equals',
                field: 'field.settlement.workflowKind',
                value: 'create',
            },
        },
        {
            id: 'section.settlement.confirm',
            titleKey: tk('card.settlement.actions.confirm'),
            fields: [
                'field.settlement.externalMessageId',
                'field.settlement.sequence',
                'field.settlement.quantity',
                'field.settlement.cash',
                'field.settlement.status',
                'field.settlement.reason',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
            visibilityCondition: {
                operator: 'equals',
                field: 'field.settlement.workflowKind',
                value: 'confirm',
            },
        },
        {
            id: 'section.settlement.correct',
            titleKey: tk('card.settlement.actions.correct'),
            fields: [
                'field.settlement.expectedQuantity',
                'field.settlement.expectedCash',
                'field.settlement.expectedDate',
                'field.settlement.reason',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
            visibilityCondition: {
                operator: 'equals',
                field: 'field.settlement.workflowKind',
                value: 'correct',
            },
        },
        {
            id: 'section.settlement.cancel',
            titleKey: tk('card.settlement.actions.cancel'),
            fields: ['field.settlement.reason'].map(fieldId),
            layout: { columns: 1, gap: 16 },
            visibilityCondition: {
                operator: 'equals',
                field: 'field.settlement.workflowKind',
                value: 'cancel',
            },
        },
    ],
    actions: [
        saveAction('create'),
        saveAction('confirm'),
        saveAction('correct'),
        saveAction('cancel'),
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: canonicalSettlementAction('create').commandId,
        successMessageKey: tk('card.settlement.saved'),
        errorMessageKey: tk('card.settlement.requestFailed'),
    },
};
